/* «Ещё варианты» вариантов на месте без React (план вехи 3, text-cover §2.2 «text-more.ts»): тот же денежный путь, что у родной формы
   (native/use-text-more.ts), — та же цепочка в sessionStorage `steos:text:more:<owner>:<root>` (readTextChain), поэтому начатое в одной
   форме продолжает другая (симметрия отката):
   - каждая группа — свой канонический заказ POST /app/orders/{source}/more (group_key и expected_price — только из политики формы), ключ
     идемпотентности и тело ложатся в цепочку до POST, неизвестность отмечается до транспорта (закрытое окно не даёт нового ключа);
   - первый определённый отказ 400 / 402 / 404 / 410 / 422 снимает ожидание; неизвестный исход — «Продолжить проверку» тем же ключом;
   - дочерний заказ опрашивается только GET раз в 2 с до done / failed (uncertain — опрос стоит); владелец проверяется перед каждым шагом;
   - цепочка не читается (битая, чужая, сменился владелец) — «заблокировано». */
import { api, endpoints } from '../../api';
import { ApiError, uuid } from '../../api/client';
import type { SpeechOrder } from '../../api/speech';
import { record } from '../studio/studio-content';
import { readTextChain } from './native/use-text-more';

type Chain = ReturnType<typeof readTextChain>;
export type MoreIntent = NonNullable<Chain['pending']>;
export const textMoreKey = (owner: number, root: number) => `steos:text:more:${owner}:${root}`;
const positive = (v: unknown): v is number => Number.isSafeInteger(v) && Number(v) > 0;
const MORE_ERROR = 'Не удалось подтвердить дополнительные варианты. Проверьте тот же запрос.';

export interface TextMoreState {
  /** Заказы групп цепочки по порядку (без корня) */
  orders: SpeechOrder[];
  pending: MoreIntent | null;
  busy: boolean;
  /** Цепочка не читается или ещё не прочитана */
  blocked: boolean;
  error: string;
}
export interface TextMore {
  /** Корень цепочки (готовый заказ вариантов на месте) и политика more формы; другой корень — цепочка заново */
  load(owner: number | undefined, root: SpeechOrder | null, cap: unknown): void;
  state(): TextMoreState;
  /** «Ещё варианты»: новая группа (или ожидающая заявка, если она есть) */
  request(): Promise<void>;
  /** «Продолжить проверку»: тот же ключ и то же тело */
  retry(): Promise<void>;
  stop(): void;
}

export function createTextMore(o: { changed(): void }): TextMore {
  let owner: number | undefined;
  let root: SpeechOrder | null = null;
  let policy: Record<string, unknown> = {};
  let loaded: { identity: string; chain: Chain; orders: SpeechOrder[] } | null = null;
  let identity = '';
  let blocked = false;
  let busy = false;
  let error = '';
  let mutex = false;
  let reading: AbortController | null = null;
  let poller: { child: number; abort: AbortController; timer: ReturnType<typeof setTimeout> | undefined } | null = null;
  let alive = true;
  const changed = () => {
    if (alive) o.changed();
  };
  const current = () => (loaded?.identity === identity ? loaded : null);
  function persist(chain: Chain, orders = current()?.orders ?? []) {
    sessionStorage.setItem(textMoreKey(chain.owner, chain.root), JSON.stringify(chain));
    loaded = { identity, chain, orders };
    poll();
    changed();
  }
  function stopPoll() {
    if (!poller) return;
    poller.abort.abort();
    clearTimeout(poller.timer);
    poller = null;
  }
  /** Опрос только GET существующего дочернего заказа; неизвестный POST восстанавливает исключительно ручной повтор */
  function poll() {
    const snapshot = current();
    const p = snapshot?.chain.pending;
    if (!p?.child || !owner || !root) {
      stopPoll();
      return;
    }
    if (poller?.child === p.child) return;
    stopPoll();
    const abort = new AbortController();
    const mine = { child: p.child, abort, timer: undefined as ReturnType<typeof setTimeout> | undefined };
    poller = mine;
    const who = owner;
    const tick = async () => {
      try {
        if ((await endpoints.me()).user.id !== who || abort.signal.aborted) return;
        const next = await endpoints.speech.get(p.child!, abort.signal);
        if (abort.signal.aborted) return;
        if (['done', 'failed'].includes(next.status)) {
          const now = current();
          if (!now) return;
          const ids = now.chain.ids.includes(next.id) ? now.chain.ids : [...now.chain.ids, next.id];
          poller = null;
          persist({ ...now.chain, ids, pending: null }, [...now.orders.filter(v => v.id !== next.id), next]);
        } else if (next.status !== 'uncertain') mine.timer = setTimeout(() => void tick(), 2000);
      } catch {
        if (!abort.signal.aborted) {
          error = MORE_ERROR;
          changed();
        }
      }
    };
    mine.timer = setTimeout(() => void tick(), 2000);
  }
  async function run(replay: boolean) {
    const snapshot = current();
    if (!owner || !root || !snapshot || blocked || mutex || (!replay && policy.available !== true)) return;
    mutex = true;
    busy = true;
    error = '';
    changed();
    let chain = snapshot.chain;
    const who = owner;
    try {
      if ((await endpoints.me()).user.id !== who) throw new Error('owner_changed');
      let p = chain.pending;
      if (!p) {
        const source = snapshot.orders.findLast(v => v.status === 'done') ?? root;
        const group = typeof policy.group_key === 'string' ? policy.group_key : '';
        if (source.status !== 'done' || !group || typeof policy.expected_price !== 'number' || policy.expected_price < 0) throw new Error('unavailable');
        p = { owner: who, root: root.id, source: source.id, key: uuid(), body: { group_key: group, expected_price: policy.expected_price }, unknown: false };
        chain = { ...chain, pending: p };
        persist(chain);
      }
      /* неизвестность — до транспорта: закрытие окна не разрешает новый ключ */
      const wasUnknown = p.unknown;
      p = { ...p, unknown: true };
      chain = { ...chain, pending: p };
      persist(chain);
      if (!p.child) {
        try {
          const response = await api.request<{ operation_order_id: number }>(`/app/orders/${p.source}/more`, { method: 'POST', body: p.body, idempotency: p.key });
          if (!positive(response.operation_order_id)) throw new Error('invalid_receipt');
          p = { ...p, child: response.operation_order_id };
          chain = { ...chain, pending: p };
          persist(chain);
        } catch (failure) {
          if (!wasUnknown && failure instanceof ApiError && [400, 402, 404, 410, 422].includes(failure.status)) {
            chain = { ...chain, pending: null };
            persist(chain);
          }
          throw failure;
        }
      }
      const next = await endpoints.speech.get(p.child!);
      if ((await endpoints.me()).user.id !== who) throw new Error('owner_changed');
      if (['done', 'failed'].includes(next.status)) {
        const now = current() ?? snapshot;
        const ids = chain.ids.includes(next.id) ? chain.ids : [...chain.ids, next.id];
        persist({ ...chain, ids, pending: null }, [...now.orders.filter(v => v.id !== next.id), next]);
      }
    } catch {
      error = MORE_ERROR;
    } finally {
      mutex = false;
      busy = false;
      changed();
    }
  }
  return {
    load(who, next, cap) {
      policy = record(cap);
      const id = `${who}:${next?.id}`;
      if (id === identity) return;
      identity = id;
      owner = who;
      root = next;
      blocked = false;
      error = '';
      loaded = null;
      stopPoll();
      reading?.abort();
      reading = null;
      if (!who || !next) return;
      const controller = new AbortController();
      reading = controller;
      void (async () => {
        try {
          const chain = readTextChain(who, next.id);
          if ((await endpoints.me()).user.id !== who || controller.signal.aborted) return;
          const orders = await Promise.all(chain.ids.map(cid => endpoints.speech.get(cid, controller.signal)));
          if (controller.signal.aborted || identity !== id) return;
          loaded = { identity: id, chain, orders };
          poll();
        } catch {
          if (!controller.signal.aborted) blocked = true;
        } finally {
          if (reading === controller) reading = null;
          if (!controller.signal.aborted) changed();
        }
      })();
    },
    state() {
      const now = current();
      return { orders: now?.orders ?? [], pending: now?.chain.pending ?? null, busy, blocked: blocked || !now, error };
    },
    request: () => run(false),
    retry: () => run(true),
    stop() {
      alive = false;
      stopPoll();
      reading?.abort();
      reading = null;
    },
  };
}
